import { useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatError } from "@/lib/api";

const SPECIES = ["dog", "cat", "rabbit", "bird", "hamster", "reptile", "other"];

export const AddPetDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({ name: "", species: "dog", breed: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Add a photo of your pet first");
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("photo", file);
    try {
      const { data } = await api.post("/pets", fd);
      toast.success(`${data.name} joined the parlor!`);
      onCreated(data);
      setForm({ name: "", species: "dog", breed: "" });
      setFile(null);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-lg" data-testid="add-pet-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add a pet</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="block cursor-pointer rounded-2xl border-2 border-dashed border-[#EAE3D9] hover:border-[#FF5A79] transition-colors overflow-hidden" data-testid="upload-pet-photo-dropzone">
            {file ? (
              <img src={URL.createObjectURL(file)} alt="preview" className="w-full h-56 object-cover" data-testid="upload-pet-photo-preview" />
            ) : (
              <div className="h-56 grid place-items-center text-center text-[#6E687A] bg-[#FAF7F2]">
                <div><ImagePlus className="h-10 w-10 mx-auto text-[#FF5A79]" /><p className="mt-2 font-semibold">Upload a clear photo</p><p className="text-xs">Face visible, good light. JPG/PNG/WEBP up to 10MB</p></div>
              </div>
            )}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="upload-pet-photo-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <div className="space-y-1.5"><Label>Name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Biscuit" className="rounded-xl" data-testid="pet-name-input" /></div>
          <div className="space-y-1.5"><Label>Species</Label>
            <div className="flex flex-wrap gap-2">
              {SPECIES.map((s) => (
                <button type="button" key={s} onClick={() => setForm({ ...form, species: s })} data-testid={`pet-species-${s}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold capitalize border transition-colors ${form.species === s ? "bg-[#1E1B26] text-white border-[#1E1B26]" : "bg-white border-[#EAE3D9] hover:border-[#1E1B26]"}`}>{s}</button>
              ))}
            </div></div>
          <div className="space-y-1.5"><Label>Breed (optional)</Label>
            <Input value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} placeholder="Corgi" className="rounded-xl" data-testid="pet-breed-input" /></div>
          <button disabled={busy} type="submit" data-testid="pet-save-button"
            className="w-full flex items-center justify-center gap-2 rounded-full bg-[#FF5A79] text-white sticker sticker-press py-3 font-bold disabled:opacity-70">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save pet
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
